import { DragLayerMonitorProps } from "@minoru/react-dnd-treeview";
import { CollectionIcon } from "@xola/icons";
import { Badge } from "@xola/ui-kit";
import React from "react";
import { CollectionNode } from "types";
import { TreeNode } from "./CollectionListHelper";

interface CustomDragPreviewProps {
    monitorProps: DragLayerMonitorProps<CollectionNode>;
}

export const CustomDragPreview = ({ monitorProps }: CustomDragPreviewProps) => {
    const { item } = monitorProps as { item: TreeNode };

    // Memoize calculations to avoid recalculating on every render during drag
    const { displayNames, collectionCount, remainingCount } = React.useMemo(() => {
        // Get nested collections from the node
        const nestedCollections = item.nestedCollections ?? [];
        const count = nestedCollections.length;

        // Take first 2 names for display
        const names = nestedCollections.slice(0, 2);
        const remaining = count - names.length;

        return {
            displayNames: names,
            collectionCount: count,
            remainingCount: remaining,
        };
    }, [item]);

    return (
        <div className="relative w-[800px]">
            {/* Stacked border effect - show 2 layers behind */}
            {collectionCount > 0 && (
                <>
                    <div
                        className="absolute bottom-0 right-0 h-full w-full rounded-lg border-[1px] border-blue-light bg-blue-lighter"
                        style={{ transform: "translate(2px, 2px)", zIndex: 1 }}
                    />
                    <div
                        className="absolute bottom-0 right-0 h-full w-full rounded-lg border-[1px] border-blue-light bg-blue-lighter"
                        style={{ transform: "translate(4px, 4px)", zIndex: 0 }}
                    />
                </>
            )}
            {/* Main drag preview */}
            <div className="relative z-10 rounded-lg border-[1px] border-blue-light bg-blue-lighter shadow-2xl">
                <div className="px-4 py-3">
                    {/* Collection name line */}
                    <div className="mb-1 flex items-center gap-2">
                        <CollectionIcon size="small" className="text-blue-darker" />
                        <span className="text-sm font-semibold text-black">
                            {item.text}
                            {displayNames.length > 0 && `, ${displayNames.join(", ")}`}
                        </span>
                    </div>
                    {/* Badge on second line */}
                    {remainingCount > 0 && (
                        <div className="ml-6">
                            <Badge size="small" color="secondary">
                                <div className="text-xs">
                                    and {remainingCount} nested collection{remainingCount === 1 ? "" : "s"}
                                </div>
                            </Badge>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

interface DropPlaceholderProps {
    depth: number;
}

export const DropPlaceholder = ({ depth }: DropPlaceholderProps) => {
    const ITEM_HEIGHT = 56; // Match the height of CollectionItem
    const DEPTH_OFFSET = 40; // Horizontal offset per depth level
    const BASE_OFFSET = 12; // Base left padding

    return (
        <div
            className="bg-blue-lightest flex items-center justify-center rounded border-2 border-dashed border-blue"
            style={{
                marginLeft: `${depth * DEPTH_OFFSET + BASE_OFFSET}px`,
                height: `${ITEM_HEIGHT}px`,
                width: `calc(100% - ${depth * DEPTH_OFFSET + BASE_OFFSET}px)`,
            }}
        >
            <span className="text-sm font-medium text-blue">Drop here</span>
        </div>
    );
};
